import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Coins, 
  CreditCard, 
  Gift,
  Check,
  Sparkles,
  Building2,
  Rocket,
  Calculator,
  Layers,
  Users,
  ChevronRight,
  XCircle,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import HUDPageHeader from '../components/HUDPageHeader';
import HUDStatCard from '../components/HUDStatCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingPage = () => {
  const { user, updateTokens } = useAuth();
  const [bonusCode, setBonusCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [simProject, setSimProject] = useState('');
  const [simServices, setSimServices] = useState([]);
  const [simLots, setSimLots] = useState(1);
  // B2B subscriptions
  const [orgInfo, setOrgInfo] = useState(null);     // { quota, organization }
  const [orgPlans, setOrgPlans] = useState([]);     // [{key, name, price_eur_monthly, monthly_quota_tokens, available, tagline}]
  const [subscribing, setSubscribing] = useState(null);
  const [canceling, setCanceling] = useState(false);

  const loadOrgState = useCallback(async () => {
    try {
      const [me, plans] = await Promise.all([
        axios.get(`${API}/organizations/me`, { withCredentials: true }),
        axios.get(`${API}/organizations/plans`),
      ]);
      setOrgInfo(me.data);
      setOrgPlans(plans.data?.plans || []);
    } catch (e) {/* user may not be eligible yet */}
  }, []);

  useEffect(() => {
    axios.get(`${API}/projects/summary`, { withCredentials: true }).then(r => setProjects(r.data)).catch(() => {});
    axios.get(`${API}/services`, { withCredentials: true }).then(r => setServices(r.data.filter(s => s.is_active))).catch(() => {});
    loadOrgState();
  }, [loadOrgState]);

  // Poll Stripe checkout status on return
  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 8) { setPaymentStatus('timeout'); return; }
    try {
      const res = await axios.get(`${API}/tokens/checkout-status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        updateTokens(res.data.new_balance);
        toast.success(`Paiement confirmé ! +${res.data.tokens_added} jetons`);
        setSearchParams({});
      } else if (res.data.payment_status === 'expired' || res.data.status === 'expired') {
        setPaymentStatus('expired');
        toast.error('Session de paiement expirée');
        setSearchParams({});
      } else {
        setPaymentStatus('processing');
        setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
      }
    } catch { setPaymentStatus('error'); }
  }, [updateTokens, setSearchParams]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPaymentStatus('processing');
      pollStatus(sessionId);
    }
  }, [searchParams, pollStatus]);

  const selectedProjectData = projects.find(p => p.project_id === simProject);
  const lotsCount = selectedProjectData?.lots_count || simLots;
  const simTotal = lotsCount * simServices.reduce((sum, sid) => {
    const svc = services.find(s => s.service_id === sid);
    return sum + (svc?.price_tokens || 0);
  }, 0);
  const simEuros = (simTotal / 10).toFixed(0);

  const packs = [
    {
      id: 'starter',
      name: 'Pack Starter',
      tokens: 500,
      price: 49,
      icon: Rocket,
      features: ['500 jetons', '5 Shoeboxes de base', 'Support email'],
      popular: false
    },
    {
      id: 'pro',
      name: 'Pack Pro',
      tokens: 1500,
      price: 129,
      icon: Building2,
      features: ['1500 jetons', '15 Shoeboxes de base', 'Support prioritaire', 'Options avancées'],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Pack Enterprise',
      tokens: 5000,
      price: 399,
      icon: Sparkles,
      features: ['5000 jetons', '50 Shoeboxes de base', 'Support dédié', 'API access', 'Formation incluse'],
      popular: false
    }
  ];

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    try {
      const response = await axios.post(
        `${API}/tokens/purchase`,
        { pack_type: packId },
        { withCredentials: true, headers: { 'x-origin-url': window.location.origin } }
      );
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'initiation du paiement");
      setPurchasing(null);
    }
  };

  const handleSubscribe = async (planKey) => {
    if (!orgInfo?.organization?.organization_id) {
      toast.error("Aucune organisation associée à votre compte");
      return;
    }
    if (!orgInfo.organization.is_owner) {
      toast.error("Seul l'admin de l'organisation peut s'abonner");
      return;
    }
    setSubscribing(planKey);
    try {
      const r = await axios.post(
        `${API}/organizations/${orgInfo.organization.organization_id}/subscribe`,
        {
          plan_key: planKey,
          success_url: `${window.location.origin}/billing?subscribed=1`,
          cancel_url: `${window.location.origin}/billing?cancelled=1`,
        },
        { withCredentials: true }
      );
      if (r.data.checkout_url) {
        if (r.data.mock) {
          toast.success("Sandbox: subscription mockée. Paiement non réel.");
          setTimeout(loadOrgState, 800);
          setSubscribing(null);
        } else {
          window.location.href = r.data.checkout_url;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur démarrage abonnement");
      setSubscribing(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!orgInfo?.organization?.organization_id) return;
    if (!window.confirm("Annuler l'abonnement à la fin de la période en cours ?")) return;
    setCanceling(true);
    try {
      await axios.post(
        `${API}/organizations/${orgInfo.organization.organization_id}/cancel-subscription`,
        {},
        { withCredentials: true }
      );
      toast.success("Annulation programmée");
      loadOrgState();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur annulation");
    } finally {
      setCanceling(false);
    }
  };

  const handleApplyCode = async () => {
    if (!bonusCode.trim()) {
      toast.error('Veuillez entrer un code');
      return;
    }

    setApplyingCode(true);
    try {
      const response = await axios.post(
        `${API}/tokens/bonus`,
        { code: bonusCode },
        { withCredentials: true }
      );
      updateTokens(response.data.new_balance);
      toast.success(response.data.message);
      setBonusCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setApplyingCode(false);
    }
  };

  return (
    <div data-hud-page data-testid="billing-page" className="fade-in">
      <HUDPageHeader
        module="Cockpit"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Facturation' }]}
        title="Facturation & Crédits"
        subtitle="Gérez vos jetons, abonnements organisation et achetez des packs."
        icon={CreditCard}
      />

      <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Payment status banner */}
      {paymentStatus === 'processing' && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3" data-testid="payment-processing">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-blue-700 font-medium">Vérification du paiement en cours...</span>
        </div>
      )}
      {paymentStatus === 'paid' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3" data-testid="payment-success">
          <Check className="w-5 h-5 text-green-600" />
          <span className="text-sm text-green-700 font-medium">Paiement confirmé ! Vos jetons ont été crédités.</span>
        </div>
      )}
      {paymentStatus === 'expired' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium">
          Session de paiement expirée. Veuillez réessayer.
        </div>
      )}

      {/* Stats top-row — cockpit billing overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="billing-stats-row">
        <HUDStatCard
          testid="stat-token-balance"
          icon={Coins}
          label="Solde actuel"
          value={user?.tokens || 0}
          hint="jetons disponibles"
          accent="#D9A05B"
        />
        <HUDStatCard
          testid="stat-org-plan"
          icon={Building2}
          label="Plan"
          value={orgInfo?.organization?.plan_key
            ? orgInfo.organization.plan_key.charAt(0).toUpperCase() + orgInfo.organization.plan_key.slice(1)
            : 'Personnel'}
          hint={orgInfo?.organization?.subscription_status === 'active' ? 'Abonnement actif' : 'Pas d\'abonnement'}
          accent="#10B981"
        />
        <HUDStatCard
          testid="stat-org-quota"
          icon={TrendingUp}
          label="Quota mensuel"
          value={orgInfo?.organization?.subscription_status === 'active'
            ? `${orgInfo.organization.monthly_used_tokens || 0}/${orgInfo.organization.monthly_quota_tokens || 0}`
            : '—'}
          hint={orgInfo?.organization ? 'Org. partagée' : 'Aucune org.'}
          accent="#A855F7"
        />
        <HUDStatCard
          testid="stat-services-count"
          icon={Wallet}
          label="Services"
          value={services.filter(s => s.is_active).length}
          hint="Catalogue actif"
          accent="#F59E0B"
        />
      </div>

      {/* ============ B2B ORGANIZATION SUBSCRIPTIONS ============ */}
      {orgInfo?.organization && (
        <div className="mb-10" data-testid="org-billing-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Organisation — {orgInfo.organization.name}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                {orgInfo.organization.members_count} membre{orgInfo.organization.members_count > 1 ? 's' : ''} ·
                Domaines : {orgInfo.organization.allowed_domains?.map(d => `@${d}`).join(', ') || '—'}
              </p>
            </div>
            {orgInfo.organization.subscription_status === 'active' && orgInfo.organization.is_owner && (
              <Button
                onClick={handleCancelSubscription}
                disabled={canceling}
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="org-cancel-sub"
              >
                {canceling ? 'Annulation…' : 'Annuler l\'abonnement'}
              </Button>
            )}
          </div>

          {/* Active subscription banner */}
          {orgInfo.organization.subscription_status === 'active' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-4" data-testid="org-active-sub">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs uppercase tracking-widest font-bold text-emerald-700">
                      Plan {orgInfo.organization.plan_key} actif
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {orgInfo.organization.monthly_used_tokens || 0} <span className="text-lg font-normal text-gray-500">/ {orgInfo.organization.monthly_quota_tokens || 0} jetons</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Cycle se termine le {orgInfo.organization.billing_cycle_end ? new Date(orgInfo.organization.billing_cycle_end).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>
                <div className="w-full sm:w-64">
                  <div className="h-2.5 bg-emerald-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${Math.min(100, ((orgInfo.organization.monthly_used_tokens || 0) / Math.max(1, orgInfo.organization.monthly_quota_tokens)) * 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-emerald-700/70 text-right mt-1">
                    {Math.round(((orgInfo.organization.monthly_used_tokens || 0) / Math.max(1, orgInfo.organization.monthly_quota_tokens)) * 100)}% utilisé
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Plan cards (always visible — current plan highlighted, others can upgrade) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 hud-stagger">
            {orgPlans.map(p => {
              const isActive = orgInfo.organization.subscription_status === 'active' && orgInfo.organization.plan_key === p.key;
              const featured = p.key === 'business';
              return (
                <div
                  key={p.key}
                  className={`relative rounded-xl border p-5 transition-all backdrop-blur-md overflow-hidden ${
                    isActive ? 'border-emerald-400/60 bg-emerald-500/[0.08] shadow-[0_0_28px_rgba(16,185,129,0.18)]' :
                    featured ? 'border-[#D9A05B]/55 bg-white/[0.05] shadow-[0_0_28px_rgba(217,160,91,0.18)]' : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
                  }`}
                  data-testid={`org-plan-${p.key}`}
                >
                  {/* Subtle accent corner glow for featured plan */}
                  {featured && !isActive && (
                    <div
                      className="pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl opacity-50"
                      style={{ background: 'radial-gradient(circle, rgba(217,160,91,0.40) 0%, rgba(217,160,91,0) 65%)' }}
                    />
                  )}
                  {featured && !isActive && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-[#D9A05B] text-black text-[10px] uppercase tracking-widest font-bold rounded-full shadow-[0_0_16px_rgba(217,160,91,0.4)]">
                      Recommandé
                    </span>
                  )}
                  {isActive && (
                    <span className="absolute -top-2.5 right-3 px-2 py-0.5 bg-emerald-500 text-black text-[10px] uppercase tracking-widest font-bold rounded-full">
                      Actif
                    </span>
                  )}
                  <h3 className="font-semibold text-lg text-white relative" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.name}</h3>
                  <p className="text-xs text-white/55 mb-3 relative">{p.tagline}</p>
                  <div className="mb-3 relative">
                    <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.price_eur_monthly}€</span>
                    <span className="text-sm text-white/50">/mois</span>
                  </div>
                  <p className="text-sm text-white/75 mb-4 relative">
                    <Coins className="inline w-4 h-4 text-[#D9A05B] mr-1.5" />
                    <strong className="text-white">{p.monthly_quota_tokens.toLocaleString('fr-FR')}</strong> jetons / mois partagés
                  </p>
                  <Button
                    onClick={() => handleSubscribe(p.key)}
                    disabled={!p.available || subscribing === p.key || isActive || !orgInfo.organization.is_owner}
                    className={`relative w-full font-medium ${
                      isActive ? 'bg-emerald-500 hover:bg-emerald-500 text-black cursor-default' :
                      featured ? 'bg-[#D9A05B] hover:bg-[#C78D46] text-black' : 'bg-white/10 hover:bg-white/15 text-white border border-white/15'
                    }`}
                    data-testid={`org-plan-subscribe-${p.key}`}
                  >
                    {!p.available ? (
                      <><XCircle className="w-4 h-4 mr-2" /> Stripe non configuré</>
                    ) : isActive ? (
                      <><Check className="w-4 h-4 mr-2" /> Plan actuel</>
                    ) : !orgInfo.organization.is_owner ? (
                      'Réservé à l\'admin'
                    ) : subscribing === p.key ? (
                      'Redirection…'
                    ) : (
                      <>S'abonner <ChevronRight className="w-4 h-4 ml-1" /></>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {orgInfo.organization.subscription_status !== 'active' && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              💡 Une fois abonné, le quota mensuel remplace les achats individuels et est partagé entre tous les {orgInfo.organization.members_count} membres de votre organisation.
            </p>
          )}

          <div className="border-t border-gray-200 my-8" />
        </div>
      )}

      {/* Pricing Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Tarification</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Description</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Coût</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 text-gray-900">1 Euro</td>
                <td className="py-3 px-4 text-right text-gray-900 font-medium">10 jetons</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 text-gray-900">Shoebox de base</td>
                <td className="py-3 px-4 text-right text-gray-900 font-medium">100 jetons</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-900">Option: Paramétrage projet / Suivi et contrôle rendu</td>
                <td className="py-3 px-4 text-right text-gray-900 font-medium">+100 jetons/shoebox</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Cost Simulator */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8" data-testid="cost-simulator">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Calculator className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900">Simulateur de devis</h2>
            <p className="text-sm text-gray-500">Estimez le coût de votre projet</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Inputs */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Projet</label>
              <Select value={simProject || 'manual'} onValueChange={(v) => { setSimProject(v === 'manual' ? '' : v); }}>
                <SelectTrigger data-testid="sim-project-select"><SelectValue placeholder="Saisie manuelle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Saisie manuelle</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>
                      {p.name} ({p.lots_count || 0} lots)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!simProject && (
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Nombre de lots</label>
                <Input
                  type="number" min={1} max={100}
                  value={simLots}
                  onChange={(e) => setSimLots(Math.max(1, parseInt(e.target.value) || 1))}
                  data-testid="sim-lots-input"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Services</label>
              <div className="space-y-2">
                {services.map(svc => (
                  <label key={svc.service_id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={simServices.includes(svc.service_id)}
                      onChange={(e) => {
                        setSimServices(prev => e.target.checked
                          ? [...prev, svc.service_id]
                          : prev.filter(id => id !== svc.service_id)
                        );
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      data-testid={`sim-svc-${svc.service_id}`}
                    />
                    <span className="flex-1 text-sm text-gray-800">{svc.name}</span>
                    <span className="text-sm font-medium text-gray-500">{svc.price_tokens} j.</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Result */}
          <div className="flex flex-col justify-between">
            <div className="bg-gray-50 rounded-xl p-5 space-y-3">
              <div className="flex justify-between text-sm text-gray-600">
                <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" />Lots</span>
                <span className="font-medium text-gray-900">{lotsCount}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Services sélectionnés</span>
                <span className="font-medium text-gray-900">{simServices.length}</span>
              </div>
              {simServices.map(sid => {
                const svc = services.find(s => s.service_id === sid);
                return svc ? (
                  <div key={sid} className="flex justify-between text-sm text-gray-500 pl-4">
                    <span>{svc.name}</span>
                    <span>{svc.price_tokens} × {lotsCount} = {svc.price_tokens * lotsCount} j.</span>
                  </div>
                ) : null;
              })}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex justify-between items-end">
                  <span className="text-sm font-medium text-gray-700">Total estimé</span>
                  <div className="text-right">
                    <span className="text-3xl font-semibold text-gray-900" data-testid="sim-total-tokens">{simTotal}</span>
                    <span className="text-sm text-gray-500 ml-1.5">jetons</span>
                  </div>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-400">Équivalent</span>
                  <span className="text-sm font-medium text-blue-600" data-testid="sim-total-euros">≈ {simEuros}€</span>
                </div>
              </div>
            </div>
            {simTotal > 0 && user?.tokens < simTotal && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                Solde insuffisant. Il vous manque <span className="font-semibold">{simTotal - (user?.tokens || 0)}</span> jetons.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pack Cards */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Acheter des jetons</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packs.map((pack) => (
            <div 
              key={pack.id}
              data-testid={`pack-${pack.id}`}
              className={`bg-white rounded-xl border-2 p-6 pack-card relative ${
                pack.popular ? 'border-blue-600' : 'border-gray-200'
              }`}
            >
              {pack.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Populaire
                  </span>
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  pack.popular ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <pack.icon className={`w-6 h-6 ${pack.popular ? 'text-blue-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{pack.name}</h3>
                  <p className="text-sm text-gray-500">{pack.tokens} jetons</p>
                </div>
              </div>

              <div className="mb-6">
                <span className="text-3xl font-semibold text-gray-900">{pack.price}€</span>
              </div>

              <ul className="space-y-2 mb-6">
                {pack.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button 
                onClick={() => handlePurchase(pack.id)}
                disabled={purchasing === pack.id}
                data-testid={`buy-${pack.id}`}
                className={`w-full ${
                  pack.popular 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
              >
                {purchasing === pack.id ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full spinner mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Acheter
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Bonus Code */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Gift className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900">Code Bonus</h2>
            <p className="text-sm text-gray-500">Entrez un code promotionnel</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Input
            value={bonusCode}
            onChange={(e) => setBonusCode(e.target.value.toUpperCase())}
            placeholder="Ex: WELCOME100"
            className="flex-1"
            data-testid="bonus-code-input"
          />
          <Button 
            onClick={handleApplyCode}
            disabled={applyingCode}
            data-testid="apply-code-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {applyingCode ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner" />
            ) : (
              'Appliquer'
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Codes disponibles pour test: WELCOME100, DEMO200, PREMIUM500, VIP1000
        </p>
      </div>
      </div>
    </div>
  );
};

export default BillingPage;
